import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { admissionCycleService, type CycleSettingsPart } from '../../../services/admissions/admissionCycleService';
import type { AdmissionCycle } from '../../../types/admissions';
import { formatMoneyFull } from '../../reports/finance/chartConfig';
import { useAuth } from '../../../context/AuthContext';

interface Props {
  open: boolean;
  /** The Draft cycle being set up. */
  cycle: AdmissionCycle;
  onClose: () => void;
  onCopied: (next: AdmissionCycle) => void;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** The parts that can be copied — the Settings tab's groups — and what each holds in `c`. */
const PARTS: Array<{ key: CycleSettingsPart; title: string; summary: (c: AdmissionCycle, currency: string) => string }> = [
  {
    key: 'seats', title: 'Seats & eligibility',
    summary: c => {
      const seats = (c.seatMatrix || []).reduce((t, e) => t + (e.totalSeats || 0), 0);
      return `${plural((c.seatMatrix || []).length, 'class')} · ${plural(seats, 'seat')} · ${plural((c.eligibility || []).length, 'eligibility rule')}`;
    },
  },
  {
    key: 'form', title: 'Application form',
    summary: c => {
      const sections = c.formTemplate?.sections || [];
      const fields = sections.reduce((t, s) => t + (s.fields || []).length, 0);
      return `${plural(sections.length, 'section')} · ${plural(fields, 'field')}`;
    },
  },
  {
    key: 'process', title: 'Selection process & merit',
    summary: c => {
      const pathways = c.pathways || [];
      if (pathways.length > 1) return `${plural(pathways.length, 'pathway')}: ${pathways.map(p => p.name).join(', ')}`;
      const p = pathways[0];
      return [
        p?.stages?.testEnabled ? `Test (${plural(p.testConfig?.papers?.length || 0, 'paper')})` : 'No test',
        p?.stages?.interviewEnabled ? `Interview (${plural(p.interviewConfig?.rounds?.length || 0, 'round')})` : 'No interview',
        plural(p?.documentChecklist?.length || 0, 'document'),
        plural(p?.scoringConfig?.meritFormula?.components?.length || 0, 'merit component'),
      ].join(' · ');
    },
  },
  {
    key: 'fees', title: 'Fees & offer',
    summary: (c, currency) => [
      c.applicationFeeAmount > 0 ? `Application fee ${formatMoneyFull(c.applicationFeeAmount, currency)}` : 'No application fee',
      plural((c.admissionFeeHeads || []).length, 'fee item'),
      `offer valid ${c.offerValidityDays ?? 14} days`,
      plural((c.offerTerms || []).length, 'offer term'),
    ].join(' · '),
  },
];

/**
 * Set up a Draft cycle from another one: pick a cycle and the parts of its
 * settings to bring over. Dates, the prospectus, the public link and
 * applications are never copied; age cut-off dates are left to set.
 */
const CopyFromCycleDialog: React.FC<Props> = ({ open, cycle, onClose, onCopied }) => {
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();
  const [cycles, setCycles] = useState<AdmissionCycle[] | null>(null);
  const [sourceId, setSourceId] = useState('');
  const [parts, setParts] = useState<Set<CycleSettingsPart>>(new Set(PARTS.map(p => p.key)));
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!open) return;
    admissionCycleService.list({ limit: 100 })
      .then(r => setCycles(r.items.filter(c => c._id !== cycle._id)))
      .catch((err: any) => enqueueSnackbar(err?.message || 'Could not load cycles', { variant: 'error' }));
  }, [open, cycle._id, enqueueSnackbar]);

  const source = useMemo(() => cycles?.find(c => c._id === sourceId) ?? null, [cycles, sourceId]);

  const hasAgeLimits = !!source && parts.has('seats')
    && (source.eligibility || []).some(r => r.ageOn && (r.ageOn.minMonths != null || r.ageOn.maxMonths != null));

  const toggle = (k: CycleSettingsPart) => setParts(prev => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const copy = async () => {
    if (!source) return;
    setCopying(true);
    try {
      const next = await admissionCycleService.copyFrom(cycle._id, source._id, PARTS.map(p => p.key).filter(k => parts.has(k)));
      enqueueSnackbar(`Settings copied from ${source.name}`, { variant: 'success' });
      onCopied(next);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not copy the settings', { variant: 'error' });
    } finally {
      setCopying(false);
    }
  };

  return (
    <Dialog open={open} onClose={copying ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Copy settings from another cycle</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            fullWidth
            label="Copy from"
            value={sourceId}
            onChange={e => setSourceId(e.target.value)}
            disabled={cycles == null}
            helperText={cycles?.length === 0 ? 'There are no other cycles yet.' : ' '}
          >
            {(cycles || []).map(c => (
              <MenuItem key={c._id} value={c._id}>{c.name} · {c.status}</MenuItem>
            ))}
          </TextField>

          {source && (
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>What to copy</Typography>
              <Stack spacing={0.5}>
                {PARTS.map(p => (
                  <Stack
                    key={p.key}
                    direction="row"
                    alignItems="flex-start"
                    spacing={1}
                    onClick={() => toggle(p.key)}
                    sx={{ cursor: 'pointer', borderRadius: 1.5, p: 0.75, '&:hover': { bgcolor: 'action.hover' } }}
                  >
                    <Checkbox size="small" checked={parts.has(p.key)} sx={{ p: 0.25 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>{p.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.summary(source, currency)}</Typography>
                    </Box>
                  </Stack>
                ))}
              </Stack>
            </Box>
          )}

          {source && (
            <Alert severity="info">
              The chosen parts replace this cycle&apos;s current settings. Dates, the prospectus and applications are not copied
              {hasAgeLimits ? '; age cut-off dates are left for you to set for this year' : ''}.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={copying} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          disableElevation
          onClick={copy}
          disabled={!source || parts.size === 0 || copying}
          startIcon={copying ? <CircularProgress size={16} color="inherit" /> : undefined}
          sx={{ textTransform: 'none' }}
        >
          {copying ? 'Copying…' : 'Copy settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CopyFromCycleDialog;
